import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, ArrowRight, Shield, CheckCircle2, User, RefreshCw, ArrowLeft, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TDSRegistrationModal = ({ isOpen, onClose, onSuccess, formType = '24Q' }) => {
  const [step, setStep] = useState(1); // 1: email entry, 2: OTP verification
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const startResendTimer = () => {
    setResendTimer(30);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!firstName.trim()) {
      setError('Please enter your first name');
      return;
    }

    if (!lastName.trim()) {
      setError('Please enter your last name');
      return;
    }

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, registration_type: 'tds' })
      });

      if (response.ok) {
        setOtpSent(true);
        setStep(2);
        startResendTimer();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    setIsSendingOtp(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/otp/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, registration_type: 'tds' })
      });

      if (response.ok) {
        startResendTimer();
        setError('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to resend OTP.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Verify OTP
      const verifyResponse = await fetch(`${API_URL}/api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });

      if (!verifyResponse.ok) {
        const data = await verifyResponse.json();
        setError(data.detail || 'Invalid OTP. Please try again.');
        setIsLoading(false);
        return;
      }

      // OTP verified - save TDS registration lead
      const fullName = `${firstName.trim()} ${lastName.trim()}`;

      try {
        await fetch(`${API_URL}/api/leads/tds-registration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName,
            formType: formType,
            source: `tds_form_${formType.toLowerCase()}_registration`,
            timestamp: new Date().toISOString()
          })
        });
      } catch (err) {
        console.log('TDS Lead API error, continuing anyway:', err);
      }

      // Save to localStorage for session
      localStorage.setItem('practiq_tds_lead_email', email);
      localStorage.setItem('practiq_tds_lead_name', fullName);
      localStorage.setItem('practiq_tds_email_verified', 'true');
      localStorage.setItem('practiq_tds_registration_session', JSON.stringify({
        email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName,
        formType,
        startedAt: new Date().toISOString(),
        emailVerified: true
      }));

      setIsLoading(false);
      onSuccess({ email, firstName: firstName.trim(), lastName: lastName.trim(), fullName, formType });
      onClose();

    } catch (err) {
      setError('Verification failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp('');
    setError('');
  };

  const features = [
    'Real TDS portal interface & workflow',
    'Form 24Q salary TDS filing practice',
    'Challan 281 linking experience'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#0F1629] border-white/10" data-testid="tds-registration-modal">
        <DialogHeader>
          <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4">
            {step === 1 ? <FileText className="w-7 h-7 text-white" /> : <Mail className="w-7 h-7 text-white" />}
          </div>
          <DialogTitle className="text-xl text-white text-center">
            {step === 1 ? `Start TDS Form ${formType} Filing Simulator` : 'Verify Your Email'}
          </DialogTitle>
          {step === 2 && (
            <p className="text-gray-400 text-sm text-center mt-2">
              We've sent a 6-digit OTP to <span className="text-blue-400">{email}</span>
            </p>
          )}
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-5 mt-4">
            {/* What you'll experience */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-blue-400 font-medium text-sm mb-2">What you'll experience:</p>
              <ul className="space-y-2">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white font-medium">First Name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setError('');
                    }}
                    placeholder="First Name"
                    className="pl-10 bg-[#1E293B] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="tds-first-name-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white font-medium">Last Name *</Label>
                <div className="relative mt-1.5">
                  <Input
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setError('');
                    }}
                    placeholder="Last Name"
                    className="bg-[#1E293B] border-white/10 text-white placeholder:text-gray-500"
                    data-testid="tds-last-name-input"
                  />
                </div>
              </div>
            </div>

            {/* Email field */}
            <div>
              <Label className="text-white font-medium">Email Address *</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="your.email@example.com"
                  className="pl-10 bg-[#1E293B] border-white/10 text-white placeholder:text-gray-500"
                  data-testid="tds-email-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">We'll send an OTP to verify your email</p>
            </div>

            {error && (
              <p className="text-red-400 text-sm">{error}</p>
            )}

            {/* Privacy note */}
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Your information is used only for this simulation. We respect your privacy.</span>
            </div>

            {/* Submit button */}
            <Button
              onClick={handleSendOtp}
              disabled={isSendingOtp}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-6"
              data-testid="tds-send-otp-btn"
            >
              {isSendingOtp ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  Send OTP
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-5 mt-4">
            {/* OTP Input */}
            <div>
              <Label className="text-white font-medium">Enter 6-digit OTP</Label>
              <Input
                type="text"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setOtp(value);
                  setError('');
                }}
                placeholder="000000"
                className="text-center text-2xl tracking-[0.5em] bg-[#1E293B] border-white/10 text-white mt-2 py-6"
                data-testid="tds-otp-input"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            {/* Resend OTP */}
            <div className="text-center">
              <button
                onClick={handleResendOtp}
                disabled={resendTimer > 0 || isSendingOtp}
                className={`text-sm ${resendTimer > 0 ? 'text-gray-500' : 'text-blue-400 hover:text-blue-300'} transition-colors`}
              >
                {resendTimer > 0 ? (
                  `Resend OTP in ${resendTimer}s`
                ) : isSendingOtp ? (
                  <span className="flex items-center justify-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Didn't receive OTP? Resend"
                )}
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 border-white/10 text-gray-300 hover:bg-white/5"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.length !== 6}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                data-testid="tds-verify-otp-btn"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Start
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TDSRegistrationModal;
